import { Feather } from "@expo/vector-icons";
import AppBar from "@mui/material/AppBar";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import Toolbar from "@mui/material/Toolbar";
import { createNavigationContainerRef, NavigationContainer } from "@react-navigation/native";
import { useState } from "react";
import Box from "@mui/material/Box";
import { View } from "react-native";
import { Text } from "react-native-paper";
import { colors } from "../../../theme";
import PgaLogo from "../../svgs/PgaLogo";
import AccountScreen from "../AccountScreen";
import AccountEditContactScreen from "../AccountEditContactScreen";
import DeferredCompScreen from "../DeferredCompScreen";
import EnrollScreen from "../EnrollScreen";
import { RootStackParamList } from "../types";
import { linking } from "./deep-links";
import { ActivitiesScreens, Stack } from "./types";
import ErrorBoundary from "../../components/ErrorBoundary";
import useGtm from "../../hooks/useGtm";
import TabBenefitsIcon from "../../svgs/TabBenefitsIcon";
import TabAccountIcon from "../../svgs/TabAccountIcon";
import { SvgProps } from "react-native-svg";
import EmailChangeScreen from "../EmailChangeScreen";
import EventScreens from "../EventScreen";
import DuesScreen from "../DuesScreen";
import DuesProxyPaymentScreen from "../DuesScreen/ProxyPaymentScreen";
import { isPublicPath } from "./utils";
import EmploymentRemoveScreen from "../EmploymentRemoveScreen";
import TabDuesIcon from "../../svgs/TabDuesIcon";
import { useFeatureFlags } from "../../hooks/useFlagr";
import { gql, useQuery } from "@apollo/client";
import ImpersonationOverlay from "../../components/ImpersonationOverlay";
import AssumeUserScreen from "../AssumeUserScreen";

const noHeaderOptions = {
  headerShown: false,
};

const WEB_DRAWER_WIDTH = 270;
const navigationRef = createNavigationContainerRef<RootStackParamList>();

const NavigatorScreen = ({ navigationChanged }: { navigationChanged?(): void }) => {
  return (
    <NavigationContainer ref={navigationRef} linking={linking}>
      <Stack.Navigator id="root" screenListeners={{ state: navigationChanged }}>
        <Stack.Group screenOptions={noHeaderOptions}>
          <Stack.Screen name="DeferredComp" component={DeferredCompScreen} options={{ title: "Deferred Compensation" }} />
          {ActivitiesScreens(noHeaderOptions)}
          {EventScreens(noHeaderOptions)}
          <Stack.Screen name="Enroll" component={EnrollScreen} />

          <Stack.Screen name="Account" component={AccountScreen} />
          <Stack.Screen name="EmailChange" component={EmailChangeScreen} />
          <Stack.Screen name="AccountEditContact" component={AccountEditContactScreen} />
          <Stack.Screen name="Dues" component={DuesScreen} />
          <Stack.Screen name="DuesProxyPayment" component={DuesProxyPaymentScreen} options={{ title: "Dues Summary" }} />
          <Stack.Screen name="EmploymentRemove" component={EmploymentRemoveScreen} options={{ title: "Remove Employment" }} />
          <Stack.Screen name="AssumeUser" component={AssumeUserScreen} options={{ title: "Assume User" }} />
        </Stack.Group>
      </Stack.Navigator>
    </NavigationContainer>
  );
};

function currentPath() {
  return new URL(window.location.href).pathname;
}

type SvgType = (props: SvgProps) => JSX.Element;

interface MenuItem {
  name: string;
  path: string;
  screen: "Account" | "DeferredComp" | "Dues";
  icon: SvgType;
}
const MENU_ITEMS: MenuItem[] = [
  { screen: "DeferredComp", icon: TabBenefitsIcon, name: "Deferred Compensation", path: "/member/deferred-comp" },
  { screen: "Dues", icon: TabDuesIcon, name: "Dues Payment", path: "/member/dues" },
  { screen: "Account", icon: TabAccountIcon, name: "Account", path: "/member/account" },
];

const DUES_QUERY = gql`
  query DuesQuery {
    duesCycle: currentDuesCycle {
      id
      order {
        id
        payable
      }
    }
  }
`;
interface DuesQuery {
  duesCycle: {
    order?: {
      payable: boolean;
    };
  };
}

function DrawerContainer({ hideDrawer }: { hideDrawer: boolean }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activePath, setActivePath] = useState(currentPath());

  const flags = useFeatureFlags();

  const { data } = useQuery<DuesQuery>(DUES_QUERY, { skip: isPublicPath() });

  const menuItems = MENU_ITEMS.filter((item) => {
    return item.screen !== "Dues" || (flags["membership-commerce-dues"] && data?.duesCycle.order?.payable);
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  let drawer = hideDrawer ? null : (
    <View>
      <Toolbar />
      <Divider />
      <List>
        {menuItems.map((item, index) => {
          const activeClass = activePath.startsWith(item.path) ? "font-500" : "";
          const Icon = item.icon;
          return (
            <View key={index}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => {
                    navigationRef.navigate(item.screen);
                    setMobileOpen(false);
                  }}
                >
                  <Icon color={colors.ink.DEFAULT} />
                  <Text className={`ml-4 text-regular-tight py-2 ${activeClass}`}>{item.name}</Text>
                </ListItemButton>
              </ListItem>
              {index < MENU_ITEMS.length - 1 && <Divider />}
            </View>
          );
        })}
      </List>
    </View>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: colors.sky.light,
          borderBottomWidth: 1,
          borderBottomStyle: "solid",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <ImpersonationOverlay />
        <Toolbar>
          {drawer && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2, display: { lg: "none" } }}>
              <Feather name="menu" size={20} />
            </IconButton>
          )}
          <PgaLogo />
        </Toolbar>
      </AppBar>
      {drawer && (
        <Box component="nav" sx={{ width: { lg: WEB_DRAWER_WIDTH }, flexShrink: { lg: 0 } }}>
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: "block", lg: "none" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: WEB_DRAWER_WIDTH },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant="permanent"
            sx={{
              display: { xs: "none", lg: "block" },
              "& .MuiDrawer-paper": { boxSizing: "border-box", width: WEB_DRAWER_WIDTH },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>
      )}
      <Box component="main" sx={{ flexGrow: 1, height: "100%", width: { lg: `calc(100% - ${WEB_DRAWER_WIDTH}px)` } }}>
        <Toolbar />
        <NavigatorScreen
          navigationChanged={() => {
            setTimeout(() => {
              setActivePath(currentPath());
            }, 100);
          }}
        />
      </Box>
    </Box>
  );
}

function appendMetaAppLink() {
  const currentPath = new URL(window.location.href).pathname;
  const excludedPaths = ["/member/events/"];
  if (excludedPaths.some((path) => currentPath.startsWith(path))) {
    return;
  }

  const meta = document.createElement("meta");
  meta.name = "apple-itunes-app";
  const productionAppId = "1659477530";
  meta.content = `app-id=${productionAppId} app-argument=${window.location.href}`;
  document.getElementsByTagName("head")[0].appendChild(meta);
}

appendMetaAppLink();

export default function Container() {
  useGtm();
  return (
    <ErrorBoundary>
      <DrawerContainer hideDrawer={isPublicPath()} />
    </ErrorBoundary>
  );
}
